import React, { useState, useEffect, useCallback } from 'react';

import useForm from '@utils/useForm';
import { isNil, isEmpty, get, find, set } from 'lodash';
import { useHistory } from "react-router-dom";
import { useTheme, ITheme } from 'src/theme';
import { Helpers } from '@utils/helpers';
import { styled } from '@linaria/react';
import { EzSaleRequest } from '@models/EzSaleRequest.model';
import { css } from '@linaria/core';

import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { updatePdfPath, updateCoverage, updateQuoteResponse, updateSQSQuotation, completeStep, updateQuoteRequest, updatePromoCode } from '@redux/slices/MLTC/step1Slice';
import { updateDeclaration, updateReadDocs, completeStep as completeStep4 } from '@redux/slices/SIO/step4Slice';
import { CoverageForm } from '@models/MLTC/Coverage.model';
import { SQSPocketRequest, LifeProfile, CoverageAttribute, Coverages, SQSPocketResponse } from '@models/MLTC/SQSPocketRequest.model';
import { postCheckEligibityForCampaign, postComputePremium, postDeclarationPDF } from '@api/index';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import Button from '@components/Button';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import StickyBox from '@components/StickyBoxMLTC';
import Text from '@components/Text';
import Input from '@components/Input';
import ContainerBlock from '@components/ContainerBlock';
import TextCollapsible from '@components/TextCollapsible';
import Divider from '@components/Divider';
import BulletList from '@components/BulletList';
import Bullet from '@components/Bullet';
import PremiumCost from '@components/PremiumCostMLTC';
import PolicyDocument from '@components/PolicyDocumentMLTC';
import Modal from '@components/Modal';
import Tag from '@components/Tag';
import Link from '@components/Link';
import IconDownload from '@assets/icon/download.svg';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import Accordion from '@components/Accordion';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Listing from '@components/Listing';
import { validate } from './service';
import { setLoadingState, setIsGlobalError } from '@redux/slices/loadingSlice';
import PlanTable from './components/PlanTable';
import BenefitList from './components/BenefitList';
import { GAAviva } from '@utils/gaAviva';
import { UOB_CONTACT_URL } from '@static/config';



const page = 'Choose Plan';

interface StyledProps {
  theme?: ITheme;
  variant: 'default' | 'alternate-color' | 'dash' | 'table';
  margin?: string | number;
  marginItems?: string | number;
}

const customRadioButton = css`
  @media screen and (max-width: 767px) {
    .radio-item {
      width: 100%;
      &:nth-child(odd) {
        &:after{
          width: 175px;
          height: 1px;
          bottom: 0;
          left: 50%;
          right: unset;
          top: unset;
          transform: translateX(-50%);
        }
      }
    }
    .radio-hover {
      bottom: auto;
      right: 0;
      height: 50%;
      width: auto;
    }
    .radio-hover.o0 {
      transform: translateY(100%);
    }
    .radio-hover.o100 {
      transform: translateY(0);
    }
  }
`

const desktopOnly = css`
  display: block; 
  @media screen and (max-width: 767px) {
    display: none;
  }
`
const mobileOnly = css`
  display: none;
  @media screen and (max-width: 767px) {
    display: block;
  }
`

const Row = styled.div<StyledProps>`  
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: ${props => props.variant === 'dash' ? '15px' : '8px'} ${props => props.variant === 'default' ? '0' : props.variant === 'table' ? 0 : '20px'};
  box-sizing: border-box;
  justify-content: space-between;
  border-bottom: ${props => props.variant === 'dash' ? `1px solid ${props.theme?.colors.grey[700]}` : 'unser'};
  margin: ${props => typeof props.marginItems === 'number' ? `${props.marginItems || 0}px` : props.marginItems || 0};
  &:nth-child(odd) {
    background-color: ${props => props.variant === 'alternate-color' ? props.theme?.colors.grey[600] : 'unset'};
  }
  &:nth-child(even) {
    background-color: ${props => props.variant === 'alternate-color' ? props.theme?.colors.white + '' : 'unset'};
  }
  &:last-child {
    border-bottom: ${props => props.variant === 'dash' ? `none` : 'unser'};
  }
`
const Col = styled.div<StyledProps>`
  width: ${props => props.variant === 'dash' ? '50%' : 'auto'};
  margin: ${props => props.margin || 0};
  flex-grow: 1;
`

const alignRight = css`
  text-align: right;
`
const noMargin = css`
  margin: 0;
`
const topMargin = css`
  margin: 10px 0 0;
`

const PromoContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: 768px) {
    flex-direction: row;
    gap: 20px;
    >div {
      justify-content: space-between;
      width: 100%;
      display: flex;
      flex-direction: column;
      &:first-child {
        max-width: 195px;
      }
    }
  }
  @media screen and (min-width: 768px) {
    >div {
      &:first-child {
        max-width: 257px;
      }
    }
  }
`;


/**
 * Local Modals
 */
const quoteInitialSample = [
  {
    name: '',
    type: 'basic',
    details: [
      { text: 'Plan', value: '-' },
      { text: 'Monthly benefit payout', value: '-' },
      { text: 'Plan&nbsp;structure', value: '-' },
      { text: 'Policy term', value: '-' },
      { text: 'Premium&nbsp;term', value: '-' },
      { text: 'Premium amount', value: '-' },
      { text: 'Promo code', value: '-' },
    ]
  },
];

const planOptions = {
  options: [
    { value: 'MLTC', description: `Singlife CareShield Standard`, key: 'myCareEnhanced' },
    { value: 'MLTCP', description: `Singlife CareShield Plus`, key: 'myCarePlusEnhanced' },
  ]
}

const planStructureOptions = {
  options: [
    { value: 'LEVEL', description: `I prefer fixed structure - same payout, same premium every year`, key: 'level' },
    { value: 'ESCALATING_2_PERCENT', description: `I prefer increasing payout and premium at 2% per year`, key: '2' },
    { value: 'ESCALATING_3_PERCENT', description: `I prefer increasing payout and premium at 3% per year`, key: '3' },
  ]
};

const premiumTermOptions = {
  options: [
    { value: 'OPTION_LIMITED', terms: '0', description: `For 0 years, until I'm 69 Age Next Birthday`, age: 0, key: 'limited' },
    { value: 'OPTION_LIFETIME', terms: '0', description: `For 0 years, until I'm 99 Age Next Birthday`, age: 0, key: 'lifetime' },
  ]
};

/**
 * MOCK RESPONSE FOR LOCAL TEST ONLY
 */
const dataMockSqs = {
  "loginId": "QnB",
  "firmClntnum": "test",
  "sourceOfBusiness": "test",
  "proposalType": "LA",
  "tables": null,
  "coverages": [
    {
      "id": "myCarePlusEnhanced",
      "type": "P",
      "name": "Singlife CareShield Plus",
      "productClass": "health",
      "coverageClass": null,
      "premiumType": "RP",
      "mode": "RP",
      "productCode": "CQG",
      "code": "EYB",
      "riderType": "test",
      "addType": "P",
      "attributes": {
        "code": "test",
        "riderType": "test",
        "planPremiumMode": "AP",
        "currency": "SGD",
        "monthlyBenefit": "2000",
        "premiumTerm": "lifetime",
        "benefitPayoutDuration": "lifetime",
        "premiumTermYears": 28,
        "escBenefit": "2",
        "promoCodeSummary": "CUS1",
        "policyTerm": 58,
        "modalPremiumNoGST": 1620,
        "modalPremium": 173.4,
        "annualPremium": "1733.40",
        "componentCode": "ENUJ,ZTAE,ZTPW,ZHP5",
        "mortalityClass": "N",
        "promoCode": "LTC1", //LTC1
        "discountEligible": true,
        "firstYrDiscountEligible": true,
        "discountedPremium": {
          "modalPremiumNoGST": 1296,
          "modalPremium": 1386.72,
          "annualPremium": "1386.72",
          "staffDiscount": 20,
          "promoCode": "LTC1", //LTC1
          "staffIndicator": "I",
          "firstYrModalPremiumNoGST": 2006,
          "firstYrModalPremium": 214.42,
          "firstYrAnnualPremium": "2146.42",
          "firstYrDisc": 15,
          "firstYrDiscType": "dollar",
          "firstYrDiscAmnt": 378.78,
          "additionalChecks": null, //"Y",
          "hideDiscounts": false
        },
        "sumAssured": 0
      }
    }
  ],
  "lifeProfiles": [
    {
      "name": "Test Name",
      "gender": "M",
      "smoker": "N",
      "dob": "28021989",
      "age": 32,
      "occupationName": "Abattoir Worker",
      "occupationCode": "A416",
      "relationshipName": "test",
      "relationshipId": "test",
      "type": "life-assured",
      "riskClass": "CLS4"
    }
  ],
  "errors": null,
  "sqsVersion": "sqs v",
  "infos": null,
  "modalPremium": 2525.2,
  "modalPremiumNoGST": 2360,
  "firstYrModalPremium": 2146.42,
  "firstYrModalPremiumNoGST": 2006,
  "staffIndicator": "A",
  "staffDiscount": 20,
  "firstYrDisc": 15,
  "firstYrDiscType": "percent",
  "firstYrDiscAmnt": 378.78,
  "additionalChecks": "Y",
  "bistacking": false
}

const createSQSPayload = ({ basicDetails, coverage, promoCodeSummary }: any): SQSPocketRequest => {
  const { day, month, year } = basicDetails.dob;
  const stringDate = `${month}/${day}/${year}`;
  const age = Helpers.getAge(new Date(stringDate));
  const lifeProfile = new LifeProfile({
    ...basicDetails,
    dob: `${day}${month}${year}`,
    age
  });
  const { promoCode, monthlyBenefit, plan, ...orders } = coverage;
  const attributePlan = new CoverageAttribute({ ...orders, monthlyBenefit: Number(monthlyBenefit), promoCodeSummary: promoCode.toUpperCase() });
  const coveragePlan = new Coverages({ id: planOptions.options[(plan === 'MLTC' ? 0 : 1)].key, type: 'P', attributes: attributePlan });
  return new SQSPocketRequest({
    lifeprofiles: [lifeProfile],
    coverages: [coveragePlan]
  });
}

const trackOnload = (premium: SQSPocketResponse | null): void => {
  const GA = GAAviva.getInstance();
  let { acquisition, form } = GA.digitalData;
  form.name = 'Choose plan';
  let { insurance, product } = acquisition;
  product = { ...product, prodID: 'MLTC' };
  if (premium) {
    product.prodName = premium.name;
    const promocode = premium.promoCode;
    insurance = {
      ...insurance,
      promocode,
      campaignCode: promocode,
      premium: `${premium.totalAftDisOriginalPremiumAmt}`,
      premiumTerm: 'Lifetime',
      sumassured: `${premium.sumAssured}`,
    }
  }
  GA.trackPageLoad({ acquisition: { ...acquisition, insurance, product } });
}

const trackFormSubmmit = (error: string = ''): void => {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}


export interface ChoosePlanProps { }


function ChoosePlan(props: ChoosePlanProps) {
  console.log(`QnB ➤ PAGE ➤ Aviva - ${page}`);
  const theme: ITheme = useTheme();
  const history = useHistory();
  const promoDescription = get(window, 'AVIVA_VARIABLE_CONFIG.promoDescription');

  const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);
  const basicDetails = useAvivaSelector(state => state.mltcStep1.basicDetails);
  const planSelection = useAvivaSelector(state => state.mltcStep1.planSelection);
  // const promoNric = useAvivaSelector(state => state.mltcStep1.promoNric);
  const initValue = useAvivaSelector(state => state.mltcStep1.coverage);
  const selectedOccupation = useAvivaSelector(state => state.sioStep1.occupation); //WIP
  const hasMyInfo = useAvivaSelector(state => state.mltcStep2.hasMyInfo);


  const [premium, setPremium] = useState<any>({});
  const [quotes, setQuotes] = useState<any>(quoteInitialSample);
  const [productSummary, setProductSummary] = useState<string>('');
  const [monthlyBenefit, setMonthlyBenefit] = useState<any>('');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isUOB, setIsUOB] = useState<boolean>(false);
  const [isShowUOB, setIsShowUOB] = useState<boolean>(false);

  const [isCalculated, setIsCalculated] = useState<boolean>(false);
  const [isAppliedPromoCode, setAppliedPromoCode] = useState<boolean>(false);
  const [checkedPromoCode, setCheckedPromoCode] = useState<string>('');
  const [isShowConfirm, setIsShowConfirm] = useState<boolean>(false);
  const [promoCodeError, setPromoCodeError] = useState<string>('');
  const [requirePromoNric, setRequirePromoNric] = useState<boolean>(!isEmpty(initValue?.promoNric) || false);
  const [hasCheckNric, setHasCheckNric] = useState<boolean>(!isEmpty(initValue?.promoNric) || false);
  const [validNric, setValidNric] = useState<boolean>(!isEmpty(initValue?.promoNric) || false);
  const [genericError, setGenericError] = useState<string | undefined>(undefined);

  const [paySelection1, setPaySelection1] = useState<boolean>(false);
  const [paySelection2, setPaySelection2] = useState<boolean>(false);
  const [paySelection3, setPaySelection3] = useState<boolean>(false);
  const [planStructure, setPlanStructure] = useState<any>({});

  const [termSelection1, setTermSelection1] = useState<boolean>(false);
  const [termSelection2, setTermSelection2] = useState<boolean>(false);
  const [premiumTerm, setPremiumTerm] = useState<any>({});

  const [loadingStack, setLoadingStack] = useState<string[]>([]);
  const [isLoadingPDF, setIsLoadingPDF] = useState<boolean>(false);

  const { handleChange, values, errors, formValid, patchValue } = useForm(new CoverageForm(initValue), () => { }, validate);

  const dispatch = useAvivaDispatch();

  const toggleModal = () => { setIsShowConfirm(!isShowConfirm); };

  const paramCC = campaignDetail?.campaignCode ? `?cc=${campaignDetail.campaignCode}` : '';
  const combineParamCC = campaignDetail?.campaignCode ? `&cc=${campaignDetail.campaignCode}` : '';

  const setLoading = useCallback((val: boolean) => {
    let stacks = [...loadingStack]
    val ? stacks.push('load') : stacks.pop();
    setLoadingStack(stacks);
    const loading = stacks.length ? true : false;
    console.log('QnB ➤ setLoading() ➤', { val, loading, isLoadingPDF, stacks });
    if (!loading) {
      if (!isLoadingPDF) {
        dispatch(setLoadingState(loading));
      }
    } else {
      dispatch(setLoadingState(loading));
    }
  }, [dispatch, setLoadingStack, isLoadingPDF, loadingStack]);

  const gotoContact = (vulnerable: boolean = false) => {
    if (isUOB) {
      window.open(UOB_CONTACT_URL, "_blank", "noopener noreferrer");
    }
    else {
      history.push(`/mltc/contact?vulnerable=${vulnerable}` + combineParamCC)
    }
  };

  const gotoUOBContact = () => {
    Helpers.clearCached();
    history.push(`/mltc/lets-get-started` + paramCC);

    window.open(UOB_CONTACT_URL, "_blank", "noopener noreferrer");
  };

  const onBack = useCallback(() => {
    history.push('/mltc-r2/basic-details' + paramCC);
  }, [history]);
  const onContinue = () => {
    if (!isAppliedPromoCode && selectedOccupation?.status === 'Decline') {
      gotoContact();
    } else {
      dispatch(completeStep(true));
      console.debug('checkedPromoCode', checkedPromoCode, window.location.href.indexOf('localhost:3000'));
      dispatch(updatePromoCode(checkedPromoCode));
      const isLocalTest = window.location.href.indexOf('localhost:3000') > 0;
      const injectParam = isLocalTest ? ((paramCC) ? '&inject=true&rental=true' : '?inject=true&rental=true') : '';
      if (!hasMyInfo) {
        history.push('/mltc-r2/my-info' + paramCC + injectParam);
      } else {
        history.push('/mltc-r2/my-info?step=1' + combineParamCC);
      }

    }
  };

  const getPlanSummary = useCallback(async (computeValues: any) => {
    console.log(`QnB ➤ getPlanSummary(computeValues) ➤ `);
    //dispatch(setLoadingState(true));
    setLoading(true);
    dispatch(setLoadingState(true));
    setIsLoadingPDF(true);

    const payload: SQSPocketRequest = createSQSPayload({ basicDetails, coverage: computeValues });
    await postDeclarationPDF({ ...payload, document: ['sum'] }, true).then(res => {
      const policyUrl = get(res, 'data');
      if (policyUrl) {
        dispatch(updatePdfPath(policyUrl)); //save filename
      }
      const url = './pdf/' + String(policyUrl).replace('/api/v1/pdf/', '');
      setProductSummary(url);

      console.log(`QnB ➤ getPlanSummary() ➤ postDeclarationPDF(request)`, {
        request: { ...payload, document: ['sum'] },
        response: res,
        url,
      });
      setIsLoadingPDF(false);
      setTimeout(() => {
        setLoading(false);//
        dispatch(setLoadingState(false));
        console.log(`QnB ➤ getPlanSummary() ➤ end of postDeclarationPDF()`, { productSummary });
      }, 50);

    }, err => {
      console.error(`QnB ➤ getPlanSummary() ➤ err`, { err });
      setIsLoadingPDF(false);
      setLoading(false); //dispatch(setLoadingState(false));
      window.location.reload();
    });
  }, [basicDetails, dispatch, productSummary, setLoading]);

  const setCalculatedPremium = useCallback((_premium: SQSPocketResponse) => {
    console.log(`QnB ➤ setCalculatedPremium(premium) ➤ `, {
      _premium,
      values,
      premiumAmount: _premium.totalAftDisOriginalPremiumAmt,
    });
    const premiumValue = _premium.mltcPremium;

    let basicTarget = find(quotes, { type: 'basic' });
    let planName = (values['plan'] === 'MLTC') ? planOptions.options[0].description : planOptions.options[1].description;
    set(basicTarget, 'details[0].value', planName);
    set(basicTarget, 'details[2].value', planStructure?.description);
    set(basicTarget, 'details[3].value', 'Lifetime');
    set(basicTarget, 'details[4].value', premiumTerm?.description);
    set(basicTarget, 'details[5].value', Helpers.toCurrency(_premium.totalAftDisOriginalPremiumAmt || 0));
    set(basicTarget, 'details[6].value', _premium.promoCode);
    setQuotes([...quotes]);
    setPremium(premiumValue);
    trackOnload(_premium);

    //#region CONSOLE LOG
    console.log(`QnB ➤ setCalculatedPremium(premium) ➤ end of function`, {
      premiumValue,
      basicTarget,
      planStructure,
    });
    //#endregion
    dispatch(updateDeclaration(null));
    dispatch(updateReadDocs(null));
    dispatch(completeStep4(false));
  }, [setQuotes, setPremium, dispatch, planStructure, premiumTerm, quotes, values]);

  const validatePromoCode = useCallback((pocket: any) => {
    console.log(`QnB ➤ validatePromoCode(pocket) ➤`, {
      pocket
    });
    const promoCodeSummary = values['promoCode'].toUpperCase();
    if (isEmpty(promoCodeSummary)) {
      setPromoCodeError('');
      setRequirePromoNric(false);
      return;
    }
    const checkedCode = pocket.checkValidPromoCode(promoCodeSummary);
    const { valid, error } = checkedCode;
    console.log(`QnB ➤ validatePromoCode(pocket) ➤ pocket.checkValidPromoCode(promoCodeSummary)`, {
      valid,
      checkedCode,
      promoCodeSummary
    });

    // invalid promo code
    if (!valid) {
      setRequirePromoNric(false);
      patchValue({ promoNric: '' });
    } else if (!requirePromoNric && isCalculated) {
      dispatch(updateCoverage(values));
      setAppliedPromoCode(true);
    } else if (requirePromoNric && validNric) {
      console.log('validatePromoCode->setAppliedPromoCode', values);
      setAppliedPromoCode(true);
    }
    setPromoCodeError(error);
    console.log(`QnB ➤ validatePromoCode(pocket) ➤ end of function`, {
      requirePromoNric,
      validNric,
      isCalculated,
      isAppliedPromoCode,
      values
    });
  }, [patchValue, values, dispatch, isAppliedPromoCode, isCalculated, requirePromoNric, validNric])

  const onCalculatePremium = useCallback(async () => {
    console.log(`QnB ➤ onCalculatePremium()`);
    if (!basicDetails) {
      onBack();
      return;
    }
    setLoading(true);

    const payload: SQSPocketRequest = createSQSPayload({ basicDetails, coverage: values });
    dispatch(updateQuoteRequest({ ...payload }));
    //setIsCalculated(true);
    try {
      const response = await postComputePremium(payload, true);
      const data = get(response, 'data.data', {});
      // MOCK data
      // const pocket = new SQSPocketResponse({
      //   ...dataMockSqs,
      //   planStructure: planStructure?.value,
      //   planStructureDesc: planStructure?.description,
      //   premiumTerm: premiumTerm?.terms,
      //   premiumTermOption: premiumTerm?.value,
      //   premiumTermDesc: premiumTerm?.description,
      //   planName: values['plan']
      // });
      // REAL data
      const pocket = new SQSPocketResponse({
        ...data,
        planStructure: planStructure?.value,
        planStructureDesc: planStructure?.description,
        premiumTerm: premiumTerm?.terms,
        premiumTermOption: premiumTerm?.value,
        premiumTermDesc: premiumTerm?.description,
        planName: values['plan']
      });
      console.log(`QnB ➤ onCalculatePremium() ➤ postComputePremium(payload, true) ➤ `, {
        request: payload,
        response,
        data,
        pocket,
        additionalCheck: pocket.additionalChecks,
        validNric,
        promoCode: values['promoCode'],
        values
      });
      setCalculatedPremium(pocket);
      setRequirePromoNric(pocket.additionalChecks || false);
      if ((pocket.additionalChecks && validNric) ||
        (pocket.additionalChecks && isEmpty(values['promoCode'])) ||
        !pocket.additionalChecks
      ) {
        setIsCalculated(true);
      }


      validatePromoCode(pocket);
      dispatch(updateCoverage(values));
      dispatch(updateSQSQuotation(pocket));
      dispatch(updateQuoteResponse(data));
      // dispatch(updateQuoteResponse(dataMockSqs));

      dispatch(updateDeclaration(null));
      dispatch(updateReadDocs(null));
      dispatch(completeStep4(false));
      //trackFormSubmmit();
      getPlanSummary(values);
      // setLoading(false);// dispatch(setLoadingState(false));
      trackFormSubmmit();
      window.scrollTo(0, 0);
    } catch (error) {
      console.error(`QnB ➤ onCalculatePremium() ➤`, { error });
      trackFormSubmmit(get(error, 'message'));
      //trackFormSubmmit(get(error, 'message'))
      dispatch(setIsGlobalError(true));
      setLoading(false); //dispatch(setLoadingState(false));
    }
  }, [dispatch, basicDetails, values, onBack, validatePromoCode, setCalculatedPremium, validNric, setIsCalculated, setRequirePromoNric, getPlanSummary, planStructure, premiumTerm, setLoading]);

  /**
   * 
   */
  const onCheckPromoCode = useCallback(async () => {
    console.log(`QnB ➤ onCheckPromoCode()`);

    const promoCodeSummary = values['promoCode'].toUpperCase();
    const payload: SQSPocketRequest = createSQSPayload({ basicDetails, coverage: values, promoCodeSummary });
    try {
      setLoading(true);//dispatch(setLoadingState(true));
      const response = await postComputePremium(payload, true);
      const data = get(response, 'data.data', {});
      // MOCK data 
      // const pocket = new SQSPocketResponse(dataMockSqs);
      // REAL data
      const pocket = new SQSPocketResponse(data);

      //#region logs..
      console.log(`QnB ➤ onCheckPromoCode() ➤ postComputePremium(payload, true) ➤ `, {
        request: payload,
        response,
        data,
        pocket
      });
      //#endregion

      // setCalculatedPremium(pocket);
      setPremium(pocket.mltcPremium); //determine promo first year or perpetual
      setRequirePromoNric(pocket.additionalChecks || false);

      const checkedCode = pocket.checkValidPromoCode(promoCodeSummary);
      const { valid, error } = checkedCode;

      console.log(`QnB ➤ onCheckPromoCode() ➤ checkValidPromoCode(promoCodeSummary) ➤ `, {
        promoCodeSummary,
        error,
        valid,
        requirePromoNric,
        isCalculated,
        values
      });

      // promo logic
      if (!valid) {
        setCheckedPromoCode('');
        setRequirePromoNric(false);
        patchValue({ promoNric: '' });
      } else if (!requirePromoNric && isCalculated) {
        dispatch(updateCoverage(values));
        setAppliedPromoCode(true);
        setCheckedPromoCode(promoCodeSummary);
      } else if (requirePromoNric) {
        dispatch(updateCoverage(values));
      } else {
        setAppliedPromoCode(true);
        setCheckedPromoCode(promoCodeSummary);
        const basicTarget = find(quotes, { type: 'basic' });
        set(basicTarget, 'details[6].value', promoCodeSummary);
        setQuotes([...quotes]);
        const nextValues = {
          ...values,
          promoCode: promoCodeSummary,
        }
        dispatch(updateCoverage(new CoverageForm(nextValues)));
      }

      // setAppliedPromoCode(valid);
      setPromoCodeError(error);
      // setCheckedPromoCode(valid ? promoCodeSummary : '');

      if (!isEmpty(promoCodeError)) {
        //patchValue({ promoCode: '' });
      }
      trackFormSubmmit();
      setLoading(false);//dispatch(setLoadingState(false));
    } catch (error) {
      trackFormSubmmit(get(error, 'message'))

      console.error(`QnB ➤ onCheckPromoCode() ➤`, { error });
      setAppliedPromoCode(false);
      setLoading(false);//dispatch(setLoadingState(false));
    }
  }, [basicDetails, dispatch, setCheckedPromoCode, isCalculated, patchValue, promoCodeError, quotes, requirePromoNric, setLoading, values])



  const resetCheck = useCallback((value: any = {}) => {
    console.log('QnB ➤ resetCheck()', { values });
    const nextValues = {
      ...values,
      ...value,
      promoCode: '',
      promoNric: ''
    }
    patchValue(nextValues);
    const basicTarget = find(quotes, { type: 'basic' });
    set(basicTarget, 'details[5].value', '-');
    set(basicTarget, 'details[6].value', '-');
    setQuotes([...quotes]);

    setRequirePromoNric(false);
    setValidNric(false);
    setIsCalculated(false);
    setHasCheckNric(false);
    setAppliedPromoCode(false);
    setCheckedPromoCode('');

    dispatch(updateCoverage(new CoverageForm(nextValues)));
  }, [values, dispatch, patchValue, quotes]);

  const additionalNricChecks = useCallback(async (nric: string, promoCode: string) => {
    console.log(`QnB ➤ additionalNricChecks(nric: '${nric}', promoCode: '${promoCode}') ➤`, {
      'isEmpty(promoCode)': isEmpty(promoCode)
    });
    setLoading(true);//dispatch(setLoadingState(true));
    const payload = new EzSaleRequest({ nric, campaignCode: promoCode });
    try {
      const response = await postCheckEligibityForCampaign(payload);
      console.debug(`QnB ➤ additionalNricChecks() ➤ postCheckEligibityForCampaign(payload) ➤`, {
        payload,
        response
      });
      /**MOCK */
      // const clientFound = false;
      const clientFound = get(response, 'data.data.clientFound', false);
      const validPeriod = get(response, 'data.data.validPeriod', false);
      console.debug(`QnB ➤ additionalNricChecks() ➤`, { clientFound, validPeriod });
      setHasCheckNric(true);

      // if not clientFound and validPeriod
      if (clientFound && validPeriod) {
        const basicTarget = find(quotes, { type: 'basic' });
        set(basicTarget, 'details[6].value', promoCode);
        setQuotes([...quotes]);

        //dispatch(updatePromoNric(nric));
        const nextValues = {
          ...values,
          promoCode,
          promoNric: nric
        }
        dispatch(updateCoverage(new CoverageForm(nextValues)));
        setValidNric(true);
        setAppliedPromoCode(true);
        setLoading(false);
        return true;
      }
      setAppliedPromoCode(false);
      setLoading(false);//dispatch(setLoadingState(false));
      return false;
    } catch (error) {
      console.error(`QnB ➤ additionalNricChecks() ➤`, { error });
      setLoading(false);//dispatch(setLoadingState(false));
      setHasCheckNric(true);
      setValidNric(true);
      return true; //bypass
    }

  }, [dispatch, quotes, setLoading, values]);

  const onCheckPromoNric = useCallback(async () => {
    const result = additionalNricChecks(values['promoNric'], values['promoCode']);
    console.log(`QnB ➤ onCheckPromoNric() ➤`, result);
  }, [additionalNricChecks, values]);

  const onChange = useCallback((event: any) => {
    handleChange(event);
    const { name, value } = event.target;
    console.log(`QnB ➤ onChange(event)`, {
      name,
      value,
      event,
      formValid,
      'errors.monthlyBenefit': errors?.monthlyBenefit,
    });

    /** Monthly field changes */
    if (name === 'monthlyBenefit') {
      console.log(`QnB ➤ onChange(event) ➤ name == monthlyBenefit ➤`, {
        formValid,
        'errors.monthlyBenefit': errors?.monthlyBenefit,
        values
      });

      setIsCalculated(false);

      let basicTarget = find(quotes, { type: 'basic' });
      if (name === 'monthlyBenefit' && (value >= 200 && value <= 5000 && value % 100 === 0)) {
        console.log('valid');
        setMonthlyBenefit(value);
        set(basicTarget, 'details[1].value', value ? Helpers.toCurrency(Number(monthlyBenefit)) : '-');
      } else if (name === 'monthlyBenefit') {
        setMonthlyBenefit(0);
        set(basicTarget, 'details[1].value', '-');
      }
      //setQuotes(newQuote);
      resetCheck();
      patchValue({
        promoCode: '',
        promoNric: '',
        monthlyBenefit: value
      });
    }

    /** Promo code field changes */
    if (name === 'promoCode') {
      console.log(`QnB ➤ onChange(event) ➤ name == promoCode ➤`, {
        formValid,
        'errors.monthlyBenefit': errors?.promoCode,
      });
      setIsCalculated(false);
      setHasCheckNric(false);
      setValidNric(false);
      setAppliedPromoCode(false);
    }

    /** Promo NRIC field changes */
    if (name === 'promoNric') {
      setIsCalculated(false);
      setHasCheckNric(false);
      setValidNric(false);
      setAppliedPromoCode(false);
      patchValue({ promoNric: value.toUpperCase() });
      console.log(`QnB ➤ onChange(event) ➤ name == promoNric ➤`, {
        formValid,
        'errors.monthlyBenefit': errors?.promoNric,
        isCalculated,
        hasCheckNric,
        promoNric: values['promoNric']
      });
    }
  }, [errors, formValid, handleChange, hasCheckNric, isCalculated, monthlyBenefit, patchValue, quotes, resetCheck, values])

  const onBlurPromo = (event: any) => {
    handleChange(event);
    const { name, value } = event.target;
    console.log(`QnB ➤ onBlurPromo(event)`, { name, value, event });

    if (isEmpty(value)) {
      setPromoCodeError('');
      setRequirePromoNric(false);
      resetCheck();
      return;
    }
    if (name === 'promoCode') {
      patchValue({ 'promoCode': value.toUpperCase() });
      onCheckPromoCode();
    }

    console.log(`QnB ➤ onBlurPromo(event) ➤ end of function`, {
      name,
      value,
      event,
      formValid,
      'errors.promoCode': errors?.promoCode
    });
  }

  const onChangeRadio = useCallback((event: any) => {
    handleChange(event);
    const { name, value } = event.target;
    console.log(`QnB ➤ onChangeRadio(event)`, { name, value, event, values });

    let basicTarget = find(quotes, { type: 'basic' })
    if (name === 'plan') {
      let planName = (value === 'MLTC') ? planOptions.options[0].description : planOptions.options[1].description;
      set(basicTarget, 'details[0].value', value ? planName : '-');
      setQuotes([...quotes]);
    }
    resetCheck({ 'plan': value });
    console.log(`QnB ➤ onChangeRadio(event) ➤ end of function`, { name, value, values });
  }, [handleChange, quotes, resetCheck, values]);

  const onChangePlanStructure = useCallback((value: any) => {
    console.log(`QnB ➤ onChangePlanStructure(value)`, { value });
    let planStructureValue: any;
    let basicTarget = find(quotes, { type: 'basic' });
    setPaySelection1(false);
    setPaySelection2(false);
    setPaySelection3(false);
    if (value === 'level') {
      setPaySelection1(true);
      planStructureValue = planStructureOptions.options[0];
    }
    else if (value === '2') {
      setPaySelection2(true);
      planStructureValue = planStructureOptions.options[1];
    }
    else if (value === '3') {
      setPaySelection3(true);
      planStructureValue = planStructureOptions.options[2];
    }
    setPlanStructure(planStructureValue);
    set(basicTarget, 'details[2].value', value ? planStructureValue?.description : '-');
    setQuotes([...quotes]);
    // patchValue({
    //   escBenefit: planStructureValue?.key
    // });
    resetCheck({ escBenefit: planStructureValue?.key });
    console.log(`QnB ➤ onChangePlanStructure(value) ➤ end of function`, { planStructureValue });
  }, [quotes, resetCheck])

  const onChangeTerm = useCallback((value: any) => {
    console.log(`QnB ➤ onChangeTerm(value)`, { value });
    let premiumTermValue: any;
    let basicTarget = find(quotes, { type: 'basic' });
    setTermSelection1(false);
    setTermSelection2(false);
    if (value === 'limited') {
      setTermSelection1(true);
      premiumTermValue = premiumTermOptions.options[0];
    }
    else if (value === 'lifetime') {
      setTermSelection2(true);
      premiumTermValue = premiumTermOptions.options[1];
    }
    setPremiumTerm(premiumTermValue);
    set(basicTarget, 'details[3].value', value ? 'Lifetime' : '-');
    set(basicTarget, 'details[4].value', value ? premiumTermValue?.description : '-');
    setQuotes([...quotes]);
    resetCheck({
      premiumTermYears: premiumTermValue?.age,
      premiumTerm: premiumTermValue?.key
    });
    console.log(`QnB ➤ onChangeTerm(value) ➤ end of function`, { premiumTermValue });
  }, [quotes, resetCheck])



  const urlParams: URLSearchParams = Helpers.getURLSearchParams();
  const getGenericError = useCallback(() => {
    const nricError = urlParams.get('nricerror');

    if (nricError === 'true') {
      setGenericError('Please enter NRIC number that matches with Myinfo');
    }
  }, [urlParams])

  useEffect(() => {
    getGenericError();

    console.log(
      'QnB ➤ useEffect() ➤',
      {
        'loading ➤ campaignDetail': campaignDetail,
        'step1Slice ➤ basicDetails': basicDetails,
        'step1Slice ➤ initValue': initValue,
        'local ➤ promoDescription': promoDescription,
        'local ➤ values': values,
        hasMyInfo
      }
    );
    if (campaignDetail && campaignDetail?.code == 'LUB1') {
      setIsUOB(true);
    }

    if (!isEmpty(basicDetails)) {
      const { month, day, year } = basicDetails?.dob || { month: '', day: '', year: '' };
      if (isEmpty(basicDetails?.dob)) onBack();
      const stringDate = `${month}/${day}/${year}`;
      const age = Helpers.getAge(new Date(stringDate));
      console.log('QnB ➤ useEffect() ➤ basicDetails is not empty ➤',
        { stringDate, age }
      );
      premiumTermOptions.options = [
        {
          value: 'OPTION_LIMITED',
          description: `For ${age >= 49 ? 20 : 69 - age} years, until I'm ${age >= 49 ? age + 20 : 69} Age Next Birthday`,
          age: age >= 49 ? 20 : 69 - age,
          key: 'limited',
          terms: `${age >= 49 ? 20 : 69 - age}`
        },
        {
          value: 'OPTION_LIFETIME',
          description: `For ${99 - age} years, until I'm 99 Age Next Birthday`,
          age: 99 - age,
          key: 'lifetime',
          terms: `${99 - age}`
        },
      ]
    } else {
      onBack();
    }

    if (!isEmpty(initValue) && !isLoaded) {
      // patch plan selection
      onChangeRadio({ target: { name: 'plan', value: initValue?.plan } });
      // patch monthly benefit input
      onChange({ target: { name: 'monthlyBenefit', value: initValue?.monthlyBenefit } });

      // patch radio buttons
      if (initValue?.escBenefit) {
        onChangePlanStructure(initValue?.escBenefit);
      }
      if (initValue?.premiumTerm) {
        onChangeTerm(initValue?.premiumTerm);
      }

      // patch promo code
      if (!isEmpty(initValue?.promoCode)) {
        patchValue({ promoCode: initValue?.promoCode });
        onCheckPromoCode();
        // setAppliedPromoCode(true);
      }

      // patch promo nric
      if (!isEmpty(initValue?.promoNric)) {
        patchValue({ promoNric: initValue?.promoNric });
        setRequirePromoNric(true);
        setValidNric(true);
        setHasCheckNric(true);
        setAppliedPromoCode(true);

      }
      const nextValues = {
        ...values,
      }
      dispatch(updateCoverage(new CoverageForm(nextValues)));
      setIsLoaded(true);
      console.log('QnB ➤ useEffect() ➤ promoNric is not empty ➤',
        {
          promoNric: initValue?.promoNric,
          requirePromoNric,
          validNric,
          hasCheckNric,
          isAppliedPromoCode
        }
      );
      trackOnload(null);
    }

  }, [isLoaded, basicDetails, dispatch, getGenericError, hasCheckNric, hasMyInfo, initValue, isAppliedPromoCode, onBack, onChange, onChangePlanStructure, onChangeRadio, onChangeTerm, onCheckPromoCode, patchValue, promoDescription, requirePromoNric, validNric, values])

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={1} label="Tell us more about you " />
          {isCalculated && (
            <StickyBox><PremiumCost {...premium} /></StickyBox>
          )}
        </FullGrid>
        <LeftGrid>
          <Heading margin="0 0 30px"> Choose a plan </Heading>
          <form onSubmit={e => { e.preventDefault() }} noValidate>
            <ContainerBlock padding={0} margin="0 0 40px" marginTablet="0 0 40px">
              <Heading margin="0 0 10px" variant="label"> A quick glance at the top features of our plans. </Heading>
              <TextCollapsible labelMore="Know more" labelLess="Know less" margin="10px 0 15px">
                <Link text="Download detailed comparison" icon={IconDownload} slotIcon="start" download="Download detailed comparison" target="_blank" rel="noopener noreferrer" url={'./docs/mltc/Plan-Comparison.pdf'} />
                <PlanTable></PlanTable>
                <Divider size={1} margin="0 0 20px" />
              </TextCollapsible>

              <RadioButtonGroup marginTablet="0 0 15px" value={values['plan']} name="plan" onChange={onChangeRadio} className={customRadioButton}>
                <RadioButton value="MLTC" text="Singlife CareShield Standard"></RadioButton>
                <RadioButton value="MLTCP" text="Singlife CareShield Plus"></RadioButton>
              </RadioButtonGroup>
              {
                (values['plan'] === 'MLTC' || values['plan'] === 'MLTCP') &&
                (<div>
                  <Heading margin="0 0 10px" variant="label"> How much additional monthly benefit do you need on top of your {planSelection} payout? </Heading>
                  <TextCollapsible labelMore="Know more" labelLess="Know less" margin="10px 0 15px">
                    <Text>According to Aviva&apos;s Long-term Care Study done in 2018, you may need an average of S$2,324 per month for the following:</Text>
                    <BenefitList></BenefitList>
                    <Text>You may use this amount as a gauge on how much you may need from Singlife - Singlife CareShield Standard or Singlife CareShield Plus minus any existing subsidies, savings or supplements you may already have.<br /><br /></Text>
                  </TextCollapsible>
                  <Text margin="0 0 15px 0">Please enter an amount between S$200 and S$5,000 (in multiples of S$100).</Text>
                  {!errors?.monthlyBenefit?.isValid && errors?.monthlyBenefit?.message && (
                    <Bullet variant="error">{errors?.monthlyBenefit?.message}</Bullet>
                  )}
                  <Input name="monthlyBenefit" invalid={!isEmpty(errors?.monthlyBenefit?.message)} maxLength={9} type="numeric" value={values['monthlyBenefit']} onChange={onChange} variant="prefix" boxLabel="S$"></Input>

                  {errors?.monthlyBenefit?.isValid && (
                    <>
                      <Heading margin="0 0 10px" variant="label"> Which payout and premium payment option you prefer? </Heading>
                      <CheckboxList>
                        <Checkbox checked={paySelection1} variant='border' type='radio' onChange={() => onChangePlanStructure('level')}>
                          <Text>I prefer fixed structure - same payout, same premium every year</Text>
                        </Checkbox>
                        <Checkbox checked={paySelection2} variant='border' type='radio' onChange={() => onChangePlanStructure('2')}>
                          <Text>I prefer increasing payout and premium at 2% per year</Text>
                        </Checkbox>
                        <Checkbox checked={paySelection3} variant='border' type='radio' onChange={() => onChangePlanStructure('3')}>
                          <Text>I prefer increasing payout and premium at 3% per year</Text>
                        </Checkbox>
                      </CheckboxList>

                      {values['escBenefit'] && (
                        <>
                          <Heading margin="0 0 10px" variant="label"> How long would you like to pay premiums for? </Heading>
                          <TextCollapsible labelMore="Know more" labelLess="Know less" margin="10px 0 15px">
                            <Text margin="0 0 15px"><strong>You can choose between two premium terms.</strong></Text>
                            <ContainerBlock>
                              <Bullet>
                                <Text><strong>Until you’re 68 Age Next Birthday:</strong> Pay for limited number of years, till the policy anniversary after you turn 68 Age Next Birthday; or for 20 years from entry age if you’re 49 Age Next Birthday and above.</Text>
                              </Bullet>
                              <Bullet>
                                <Text><strong>Until you’re 99 Age Next Birthday:</strong> Pay till the policy anniversary after you turn 98 Age Next Birthday.</Text>
                              </Bullet>
                            </ContainerBlock>
                          </TextCollapsible>
                          <CheckboxList>
                            <Checkbox checked={termSelection1} variant='border' type='radio' onChange={() => onChangeTerm('limited')}>
                              <Text>{premiumTermOptions.options[0].description}</Text>
                            </Checkbox>
                            <Checkbox checked={termSelection2} variant='border' type='radio' onChange={() => onChangeTerm('lifetime')}>
                              <Text>{premiumTermOptions.options[1].description}</Text>
                            </Checkbox>
                          </CheckboxList>

                          {values['premiumTermYears'] && (
                            <>
                              <Heading variant="sub" fontWeight={600} margin="60px 0 15px"> Check for promos and discounts</Heading>
                              <Divider margin="0 0 30px" />
                              <Text margin="0 0 20px" dangerouslySetInnerHTML={{ __html: (!isEmpty(promoDescription)) ? promoDescription : `` }}></Text>
                              {/* 
                              For existing MyCare customers:<br />Use <strong>CUS1</strong> to enjoy 15% first year discount and 20% perpetual discount
                              <Text margin="0 0 20px">For existing MyCare customers:<br />Use <strong>OVWT</strong> to enjoy $20 first year discount and 15% perpetual discount</Text> 
                              */}

                              <PromoContainer>
                                <div>
                                  <div>
                                    <Heading variant='label'>Enter your promo code</Heading>
                                    {isEmpty(isEmpty) && isEmpty(promoCodeError) && isAppliedPromoCode && ((requirePromoNric && validNric) || !requirePromoNric) && <Bullet variant="success" className={noMargin}> Promo code is successfully <br />applied.</Bullet>}
                                    {!isEmpty(promoCodeError) && <Bullet variant="error" className={noMargin}> {promoCodeError} </Bullet>}
                                    {(hasCheckNric && !validNric && isEmpty(promoCodeError)) && <Bullet variant="error" className={noMargin}> You&apos;re not eligible for this<br />promotion </Bullet>}
                                  </div>
                                  <Input
                                    name="promoCode"
                                    textTransform="uppercase"
                                    maxLength={6}
                                    value={values['promoCode']}
                                    invalid={!isEmpty(promoCodeError)}
                                    onChange={onChange}
                                    onBlur={onBlurPromo}
                                    margin="15px 0 20px"></Input>

                                </div>
                                <div>
                                  <div>
                                    <Heading variant="label" disabled={!requirePromoNric}>Enter your NRIC number</Heading>
                                    <Text fontSize={16} margin={0} disabled={!requirePromoNric}>We will check our records to see if you are eligible</Text>
                                    {
                                      !errors?.promoNric?.isValid &&
                                      errors?.promoNric?.message &&
                                      (<Bullet variant="error" className={topMargin}>{errors?.promoNric?.message}</Bullet>)
                                    }
                                    {
                                      (!isEmpty(genericError) && !isNil(genericError)) && (
                                        <Bullet variant="error" className={topMargin}>{genericError}</Bullet>
                                      )
                                    }
                                  </div>
                                  <Input
                                    disabled={!requirePromoNric}
                                    name="promoNric"
                                    textTransform="uppercase"
                                    maxLength={24}
                                    value={values['promoNric']}
                                    invalid={!errors?.promoNric?.isValid && requirePromoNric}
                                    onChange={onChange}
                                    margin="15px 0 20px"></Input>
                                </div>
                              </PromoContainer>
                              {
                                requirePromoNric &&
                                <Button
                                  disabled={
                                    hasCheckNric ||
                                    isNil(values['promoNric']) ||
                                    isEmpty(values['promoNric']) ||
                                    !errors?.promoNric?.isValid ||
                                    isAppliedPromoCode}
                                  onClick={onCheckPromoNric}
                                  margin="0"
                                  label="Check now"></Button>
                              }
                            </>

                          )}
                        </>)}

                    </>)}

                </div>)
              }
            </ContainerBlock>
          </form>


          <ContainerBlock className={desktopOnly}>

            <Divider size={1} margin="0 0 0" />
            <ContainerBlock padding={20} paddingTablet={20} margin={0} className={desktopOnly}>
            <Accordion title="Important notes" isExpanded>
              <ContainerBlock>
                <Bullet>
                  <Text>Check by using <Link text='budget calculator' target="_blank" rel="noopener noreferrer" url='https://www.moneysense.gov.sg/financial-tools/budget-calculator' /> if the premium that you will pay is affordable based on your current income and expenditure</Text>
                </Bullet>
                <Bullet>
                  <Text>Premium is based on the entry Age Next Birthday (ANB) and gender for the selected premium term. Premiums are not guaranteed.</Text>
                </Bullet>
                <Bullet>
                  Anyone who pays for, or is insured under Singlife CareShield Standard / Singlife CareShield Plus (Supplement) is not eligible for Additional Premium Support (APS) from the Government*. If you are currently receiving APS to pay for your MediShield Life and/or CareShield Life premiums, and you choose to be insured under this Supplement policy, you will stop receiving APS. This applies even if you are not the person paying for this Supplement policy. In addition, if you choose to be insured under this Supplement policy, the person paying for the Supplement will stop receiving APS, if he or she is currently receiving APS.
                </Bullet>
                <Heading margin={"10px 0 30px"} marginTablet={"10px 0 30px"} variant='desc'>
                  *APS is for families who need assistance with MediShield Life and/or CareShield Life premiums, even after receiving premium subsidies and making use of MediSave to pay for these premiums.
                </Heading>
              </ContainerBlock>
            </Accordion>
            </ContainerBlock>
            
            <Divider size={4} margin="0 0 40px" />

            {isCalculated && (<Button margin="0 0 20px" label="Continue" onClick={toggleModal} />)}
            <Button margin="0 0 40px" variant="secondary" label="Back" onClick={onBack} />
          </ContainerBlock>

        </LeftGrid>

        <EmptyGrid></EmptyGrid>

        <RightGrid>
          <ContainerBlock
            borderTop={`3px solid ${theme.colors.primary}`}
            boxShadow="0 2px 2px 0 rgba(204, 204, 204, 0.5)"
            background={theme.colors.grey[200]}
            padding={20}
            paddingTablet="20px 30px 10px">
            <Heading margin="0 0 20px" color={theme.colors.grey[400]} variant="smallTitle" fontWeight={600}>Your quote details</Heading>
            <Text>The maximum CPF MediSave deduction limit is S$600 per person per calendar year. The balance premiums(s) or any insufficient amount in the CPF MediSave account will be paid via your selected payment method.</Text>
            <Divider size={1} margin="20px 0 20px" />
            <Listing marginItems="0 0 4px" sources={[quotes[0].details[0]]} margin="0 0 12px" />

            <Listing marginItems="0 0 4px" sources={[{ text: 'Monthly benefit payout', value: monthlyBenefit ? Helpers.toCurrency(Number(monthlyBenefit)) : '-' }]} margin="0 0 12px" />
            {(monthlyBenefit !== false) && <TextCollapsible labelMore="Additional care benefits" labelLess="Additional care benefits" margin="10px 0 15px" expanded={true}>
              <ContainerBlock
                background={theme.colors.white}
                padding={20}
                paddingTablet="20px 20px 10px">
                <Text margin="0 0 10px" fontWeight={600}>Additional care benefits*</Text>
                <Listing marginItems="0 0 4px" variant='table' sources={[{ text: 'Lump-sum benefit', value: Helpers.toCurrency(Number(monthlyBenefit * 3)) }]} margin="0 0 12px" />
                <Text margin="-12px 0 10px" fontWeight={400} fontSize={14}>3X of first monthly benefit payout</Text>

                <Listing marginItems="0 0 4px" variant='table' sources={[{ text: 'Rehabilitation benefit', value: values['plan'] === 'MLTCP' ? '-' : Helpers.toCurrency(Number(monthlyBenefit * 50 / 100)) }]} margin="0 0 12px" />
                <Text margin="-12px 0 0" fontWeight={400} fontSize={14}>50% of monthly benefit payout</Text>
                <Text margin="0 0 10px" fontWeight={400} fontSize={14}>(for Singlife CareShield Standard only)</Text>

                <Listing marginItems="0 0 4px" variant='table' sources={[{ text: 'Caregiver relief benefit', value: Helpers.toCurrency(Number(monthlyBenefit * 60 / 100)) }]} margin="0 0 12px" />
                <Text margin="-12px 0 10px" fontWeight={400} fontSize={14}>60% of monthly benefit payout</Text>

                <Listing marginItems="0 0 4px" variant='table' sources={[{ text: 'Dependant care benefit', value: Helpers.toCurrency(Number(monthlyBenefit * 20 / 100)) }]} margin="0 0 12px" />
                <Text margin="-12px 0 10px" fontWeight={400} fontSize={14}>20% of monthly benefit payout</Text>

                <Listing marginItems="0 0 4px" variant='table' sources={[{ text: 'Death benefit', value: Helpers.toCurrency(Number(monthlyBenefit * 3)) }]} margin="0 0 12px" />
                <Text margin="-12px 0 10px" fontWeight={400} fontSize={14}>3X monthly benefit payout</Text>

                <Text margin="20px 0 10px" fontWeight={600}>Waiver of Premium</Text>
                <Text margin="20px 0 10px" fontWeight={600}>Guaranteed Issuance Option</Text>

              </ContainerBlock>
            </TextCollapsible>}
            <Listing marginItems="0 0 4px" sources={[quotes[0].details[2]]} margin="0 0 12px" />
            <Listing marginItems="0 0 4px" sources={[quotes[0].details[3]]} margin="0 0 12px" />
            <Listing marginItems="0 0 4px" sources={[quotes[0].details[4]]} margin="0 0 12px" />
            <Listing marginItems="0 0 4px" sources={[quotes[0].details[5]]} margin="0 0 12px" />
            <Listing marginItems="0 0 4px" sources={[quotes[0].details[6]]} margin="0 0 12px" />
            <Container>
              {(premium.firstYearDiscountEligible && ((values['promoCode'] && isAppliedPromoCode) || isCalculated)) &&
                (<Row variant={'table'}>
                  <Col variant={'default'}>
                    <Tag variant="promo">1st Year Discount</Tag>
                  </Col>
                  <Col variant={'default'} className={alignRight}><strong>{premium.totalFirstYearDiscount}</strong></Col>
                </Row>)}
              {(premium.perpetualDiscountEligible && ((values['promoCode'] && isAppliedPromoCode) || isCalculated)) &&
                (<Row variant={'table'}>
                  <Col variant={'default'}>
                    <Tag variant="promo">Perpetual Discount</Tag>
                  </Col>
                  <Col variant={'default'} className={alignRight}><strong>{premium.totalPerpetualDiscount}</strong></Col>
                </Row>)}
            </Container>
            <Divider size={1} variant='dashed' margin="20px 0 20px" />
            <Text margin="0 0 10px" fontWeight={400} fontSize={14}>* Please refer to the Product Summary for more details.</Text>
          </ContainerBlock>

          {/* <Quote details={quotes} /> || (requirePromoNric && !validNric) || !(requirePromoNric && errors?.promoNric?.isValid) */}
          {/* <Button margin="0 0 20px" disabled={!formValid || (!isEmpty(values['promoCode']) && !isAppliedPromoCode)} label="Calculate your premium" onClick={onCalculatePremium} />*/}
          {
            isCalculated &&
            <PolicyDocument url={productSummary} />
          }
          {
            (!isCalculated) &&
            (<Button
              margin="0 0 20px"
              disabled={!formValid ||
                !isEmpty(promoCodeError) ||
                (requirePromoNric && !errors?.promoNric?.isValid) ||
                (requirePromoNric && values['promoCode'] && !validNric) ||
                (values['escBenefit'] === false) ||
                (values['premiumTermYears'] === false)}
              label="Calculate your premium"
              onClick={onCalculatePremium} />)}

          <ContainerBlock className={mobileOnly}>

            <Divider size={1} margin="0 0 20px" />
            <Accordion title="Important notes" isExpanded>
              <ContainerBlock>
                <Bullet>
                  <Text>Premium is based on the entry Age Next Birthday (ANB) and gender for the selected premium term. Premiums are not guaranteed.</Text>
                </Bullet>
                <Bullet>
                  Anyone who pays for, or is insured under Singlife CareShield Standard / Singlife CareShield Plus (Supplement) is not eligible for Additional Premium Support (APS) from the Government*. If you are currently receiving APS to pay for your MediShield Life and/or CareShield Life premiums, and you choose to be insured under this Supplement policy, you will stop receiving APS. This applies even if you are not the person paying for this Supplement policy. In addition, if you choose to be insured under this Supplement policy, the person paying for the Supplement will stop receiving APS, if he or she is currently receiving APS.
                </Bullet>
                <Heading margin={"10px 0 30px"} marginTablet={"10px 0 30px"} variant='desc'>
                  *APS is for families who need assistance with MediShield Life and/or CareShield Life premiums, even after receiving premium subsidies and making use of MediSave to pay for these premiums.
                </Heading>
              </ContainerBlock>
            </Accordion>
            <Divider size={4} margin="0 0 40px" />

            {isCalculated && (<Button margin="0 0 20px" label="Continue" onClick={toggleModal} />)}
            <Button margin="0 0 40px" variant="secondary" label="Back" onClick={onBack} />
          </ContainerBlock>


          <Modal show={isShowConfirm} onClose={toggleModal}>
            <Heading variant="smallTitle" fontWeight={600} margin="0 0 20px" marginTablet="0 0 30px">Important notice</Heading>
            <Heading variant="label" fontWeight="normal" margin="0 0 15px" marginTablet="0 0 10px">Do you meet at least two of the following conditions?</Heading>
            <BulletList margin="0 0 30px">
              <Bullet><Text>You’re below 62 years old.</Text></Bullet>
              <Bullet><Text>You’re proficient in both spoken and written English.</Text></Bullet>
              <Bullet><Text>You’ve attained at least a GCE N-Level or O-Level certification, or equivalent academic qualification.</Text></Bullet>
            </BulletList>
            <Button margin="0 0 20px" label="Yes" onClick={onContinue} />
            <Button variant="secondary" label="No" margin={0} onClick={() => { gotoContact(true) }} />
          </Modal>

          <Modal show={isShowUOB} onClose={() => { setIsShowUOB(false) }}>
            <Text margin={'0px 0 30px'}>You will be redirected to our appointed intermediary - United Overseas Bank Limited (“UOB”) site to submit the contact me form.</Text>
            <Button margin="0 0 20px" label="Proceed" onClick={gotoUOBContact} />
            <Button variant="secondary" label="Cancel" margin={0} onClick={() => { setIsShowUOB(false) }} />
          </Modal>
        </RightGrid>
      </ContainerGrid>

    </Container>
  );
};

export default ChoosePlan;
